import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { useAiStreamSse } from "@/common/hooks/useAiStreamSse";
import { useAuth } from "@/common/hooks/useAuth";
import { useToast } from "@/common/hooks/useToast";
import {
  getActiveGenerationJobs,
  getGenerationJob,
  dismissGenerationJob,
  type GenerationJobDto,
} from "@/common/services/test-gen/test-gen.api";

export type ReqGenStatus = "running" | "complete" | "error";

/** A single AI-generated requirement (loosely typed to avoid coupling to the modal). */
export interface ReqGenItem {
  id: string;
  title: string;
  description: string;
  type: string;
  priority: string;
  folder?: string;
  tags?: string[];
  selected?: boolean;
}

/**
 * A backgroundable AI *requirement* generation session — the requirement-side twin
 * of BackgroundGenerationProvider. The backend runs requirement generation
 * fire-and-forget and buffers SSE events by `generationId`, and now also persists
 * a durable `ai_generation_jobs` row (kind = "requirement"), so this snapshot can
 * survive a modal close, a hard refresh, a second tab, or the SSE replay window
 * expiring.
 */
export interface ReqGenSession {
  generationId: string;
  status: ReqGenStatus;
  progress: number;
  message: string | null;
  /** Accumulated generated requirements (flat list). */
  requirements: ReqGenItem[];
  error: string | null;
  startedAt: number;
  /** The user's prompt, to restore the generator view on reopen. */
  prompt?: string;
  /** True when restored from the DB (refresh / new tab / broadcast). */
  restored?: boolean;
}

interface BeginInit {
  generationId: string;
  prompt?: string;
}

type MirrorPatch = Partial<
  Pick<
    ReqGenSession,
    "status" | "progress" | "message" | "requirements" | "error"
  >
>;

interface ReqGenActions {
  begin: (init: BeginInit) => void;
  mirror: (patch: MirrorPatch) => void;
  setOwnerMounted: (mounted: boolean) => void;
  clear: () => void;
  getSession: () => ReqGenSession | null;
}

interface ReqGenState {
  session: ReqGenSession | null;
  resultCount: number;
}

const ReqGenActionsContext = createContext<ReqGenActions | null>(null);
const ReqGenStateContext = createContext<ReqGenState | null>(null);

const BROADCAST_CHANNEL = "autopilotqa:req-gen";
const STALE_RUNNING_MS = 10 * 60 * 1000;
const TOAST_DEDUPE_PREFIX = "reqgen:toasted:";
const TOAST_DEDUPE_TTL_MS = 10 * 60 * 1000;

/** Cross-tab toast de-dup so only the first tab to finish a shared gen notifies. */
function claimToast(generationId: string): boolean {
  try {
    const key = TOAST_DEDUPE_PREFIX + generationId;
    const now = Date.now();
    const existing = window.localStorage.getItem(key);
    if (existing && now - Number(existing) < TOAST_DEDUPE_TTL_MS) return false;
    window.localStorage.setItem(key, String(now));
    for (let i = window.localStorage.length - 1; i >= 0; i--) {
      const k = window.localStorage.key(i);
      if (k && k.startsWith(TOAST_DEDUPE_PREFIX)) {
        const v = Number(window.localStorage.getItem(k));
        if (!v || now - v > TOAST_DEDUPE_TTL_MS)
          window.localStorage.removeItem(k);
      }
    }
    return true;
  } catch {
    return true;
  }
}

function countRequirements(list: ReqGenItem[] | undefined): number {
  return Array.isArray(list) ? list.length : 0;
}

/** Build a session snapshot from a persisted job DTO (metadata only; no result). */
function sessionFromJob(job: GenerationJobDto): ReqGenSession {
  const status: ReqGenStatus =
    job.status === "completed"
      ? "complete"
      : job.status === "running"
        ? "running"
        : "error";
  return {
    generationId: job.generationId,
    status,
    progress: job.progress ?? (status === "complete" ? 100 : 0),
    message: job.message ?? null,
    requirements: [],
    error: job.error ?? null,
    startedAt: job.createdAt ? new Date(job.createdAt).getTime() : Date.now(),
    prompt: job.prompt ?? undefined,
    restored: true,
  };
}

export function RequirementBackgroundGenerationProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { showToast } = useToast();
  const { userPreferenceData } = useAuth();
  const versionId: number | string | undefined =
    userPreferenceData?.data?.versionId;

  const [session, setSession] = useState<ReqGenSession | null>(null);
  const [ownerMounted, setOwnerMountedState] = useState(false);
  const ownerMountedRef = useRef(false);

  const sessionRef = useRef<ReqGenSession | null>(null);
  sessionRef.current = session;

  const tabIdRef = useRef<string>(
    `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
  );
  const channelRef = useRef<BroadcastChannel | null>(null);

  const broadcast = useCallback(
    (kind: string, payload: Record<string, any>) => {
      try {
        channelRef.current?.postMessage({
          tabId: tabIdRef.current,
          kind,
          ...payload,
        });
      } catch {
        // channel closed / structured-clone failure — non-fatal
      }
    },
    [],
  );

  // Fetch the persisted result list for a completed generation and merge it in.
  const hydrateResult = useCallback((generationId: string) => {
    getGenerationJob(generationId)
      .then((res) => {
        const result = (res?.data?.job as any)?.result;
        if (!Array.isArray(result)) return;
        setSession((prev) =>
          prev && prev.generationId === generationId
            ? {
                ...prev,
                requirements: result,
                status: "complete",
                progress: 100,
              }
            : prev,
        );
      })
      .catch(() => {
        /* best-effort */
      });
  }, []);

  const begin = useCallback(
    (init: BeginInit) => {
      const next: ReqGenSession = {
        generationId: init.generationId,
        status: "running",
        progress: 0,
        message: null,
        requirements: [],
        error: null,
        startedAt: Date.now(),
        prompt: init.prompt,
      };
      setSession(next);
      ownerMountedRef.current = true;
      setOwnerMountedState(true);
      broadcast("begin", { session: { ...next, requirements: [] } });
    },
    [broadcast],
  );

  const mirror = useCallback((patch: MirrorPatch) => {
    setSession((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  const setOwnerMounted = useCallback((mounted: boolean) => {
    ownerMountedRef.current = mounted;
    setOwnerMountedState(mounted);
  }, []);

  const clear = useCallback(() => {
    const gid = sessionRef.current?.generationId;
    setSession(null);
    ownerMountedRef.current = false;
    setOwnerMountedState(false);
    if (gid) {
      void dismissGenerationJob(gid).catch(() => {});
      broadcast("clear", { generationId: gid });
    }
  }, [broadcast]);

  const getSession = useCallback(() => sessionRef.current, []);

  // Only run our OWN subscription when the modal is closed and the session is
  // still running — otherwise the modal owns the stream (single connection).
  const providerGenerationId =
    session && session.status === "running" && !ownerMounted
      ? session.generationId
      : null;

  useAiStreamSse({
    generationId: providerGenerationId,
    onStatusChange: (message, progress) => {
      setSession((prev) => (prev ? { ...prev, message, progress } : prev));
    },
    onComplete: (data) => {
      setSession((prev) => {
        if (!prev) return prev;
        const finalList: ReqGenItem[] = Array.isArray(data)
          ? data
          : prev.requirements;
        const count = countRequirements(finalList);
        if (!ownerMountedRef.current && claimToast(prev.generationId)) {
          showToast({
            title: "Requirements ready",
            description:
              count > 0
                ? `${count} requirement${count === 1 ? "" : "s"} generated. Open the generator to review.`
                : "AI generation finished. Open the generator to review.",
            variant: "success",
          });
        }
        broadcast("terminal", {
          generationId: prev.generationId,
          status: "complete",
          resultCount: count,
        });
        return {
          ...prev,
          status: "complete",
          progress: 100,
          message: "Complete",
          requirements: finalList,
        };
      });
    },
    onError: (message) => {
      setSession((prev) => {
        if (!prev) return prev;
        if (!ownerMountedRef.current && claimToast(prev.generationId)) {
          showToast({
            title: "AI generation failed",
            description:
              message || "Something went wrong while generating requirements.",
            variant: "error",
          });
        }
        broadcast("terminal", {
          generationId: prev.generationId,
          status: "error",
        });
        return {
          ...prev,
          status: "error",
          error: message || "Generation failed",
        };
      });
    },
  });

  // ── Rehydrate on mount (refresh / expired replay window) ────────────────────
  const didRehydrateRef = useRef(false);
  useEffect(() => {
    if (didRehydrateRef.current) return;
    if (sessionRef.current) {
      didRehydrateRef.current = true;
      return;
    }
    if (!userPreferenceData?.data) return;
    didRehydrateRef.current = true;

    getActiveGenerationJobs(versionId ?? null, "requirement")
      .then((res) => {
        if (sessionRef.current) return;
        const jobs = res?.data?.jobs ?? [];
        if (!jobs.length) return;
        const running = jobs.find((j) => j.status === "running");
        const chosen = running ?? jobs.find((j) => j.status === "completed");
        if (!chosen) return;

        if (chosen.status === "running") {
          const updatedAt = chosen.updatedAt
            ? new Date(chosen.updatedAt).getTime()
            : 0;
          if (updatedAt && Date.now() - updatedAt > STALE_RUNNING_MS) {
            void dismissGenerationJob(chosen.generationId).catch(() => {});
            return;
          }
          setSession(sessionFromJob(chosen));
          return;
        }

        const restored = sessionFromJob(chosen);
        restored.progress = 100;
        setSession(restored);
        if (chosen.hasResult) hydrateResult(chosen.generationId);
      })
      .catch(() => {
        /* best-effort */
      });
  }, [userPreferenceData, versionId, hydrateResult]);

  // ── Cross-tab sync ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") return;
    const channel = new BroadcastChannel(BROADCAST_CHANNEL);
    channelRef.current = channel;

    channel.onmessage = (evt: MessageEvent) => {
      const msg = evt.data;
      if (!msg || msg.tabId === tabIdRef.current) return;

      if (msg.kind === "begin" && msg.session) {
        const incoming = msg.session as ReqGenSession;
        const cur = sessionRef.current;
        if (!cur || cur.generationId === incoming.generationId) {
          setSession({ ...incoming, requirements: [], restored: true });
        }
        return;
      }

      if (msg.kind === "terminal") {
        const cur = sessionRef.current;
        if (!cur || cur.generationId !== msg.generationId) return;
        if (msg.status === "complete") {
          setSession((prev) =>
            prev
              ? {
                  ...prev,
                  status: "complete",
                  progress: 100,
                  message: "Complete",
                }
              : prev,
          );
          if (!countRequirements(sessionRef.current?.requirements)) {
            hydrateResult(msg.generationId);
          }
        } else {
          setSession((prev) =>
            prev
              ? {
                  ...prev,
                  status: "error",
                  error: prev.error ?? "Generation failed",
                }
              : prev,
          );
        }
        return;
      }

      if (msg.kind === "clear") {
        const cur = sessionRef.current;
        if (cur && cur.generationId === msg.generationId) {
          setSession(null);
          ownerMountedRef.current = false;
          setOwnerMountedState(false);
        }
      }
    };

    return () => {
      channel.close();
      channelRef.current = null;
    };
  }, [hydrateResult]);

  const actions = useMemo<ReqGenActions>(
    () => ({ begin, mirror, setOwnerMounted, clear, getSession }),
    [begin, mirror, setOwnerMounted, clear, getSession],
  );

  const state = useMemo<ReqGenState>(
    () => ({ session, resultCount: countRequirements(session?.requirements) }),
    [session],
  );

  return (
    <ReqGenActionsContext.Provider value={actions}>
      <ReqGenStateContext.Provider value={state}>
        {children}
      </ReqGenStateContext.Provider>
    </ReqGenActionsContext.Provider>
  );
}

/** Actions only (stable) — safe to use without re-rendering on progress ticks. */
export function useRequirementBgActions(): ReqGenActions | null {
  return useContext(ReqGenActionsContext);
}

/** Reactive session state — re-renders on every progress tick (indicator only). */
export function useRequirementBgState(): ReqGenState {
  const ctx = useContext(ReqGenStateContext);
  return ctx ?? { session: null, resultCount: 0 };
}
